/**
 * ⭐ Label untuk **setiap nilai enum `@osbpos/contract`**.
 *
 * Ini gerbang yang khas POS (`design/I18N.md` §5). Kontrak memisahkan **kode**
 * (`liability_release`) dari **tampilan** — dan pemisahan itu hanya berguna kalau tampilannya
 * benar-benar ada. Nilai enum tanpa label berarti kasir melihat `liability_release` di layar
 * saat jam sibuk.
 *
 * ⇒ `scripts/i18n-check.mjs` memverifikasi cakupannya **terhadap kontrak**, bukan terhadap ingatan.
 *
 * Tiering: `taxKind` menyentuh nama pajak Indonesia ⇒ sebagian **T1** (tetap Indonesia di kedua
 * mode). `tableKind`/`orderType` operasional ⇒ sebagian **T3** (tetap Inggris di kedua mode).
 */
export const enumId = {
  tenderType: {
    cash: 'Tunai',
    clearing: 'Kartu/QRIS',
    liability_release: 'Voucher/Saldo',
    non_tender: 'Cuma-cuma',
    receivable: 'Bon/Piutang',
  },
  taxKind: {
    local_tax: 'Pajak Daerah',
    vat: 'PPN',
    service_charge: 'Service Charge',
    withholding: 'Potong Pajak',
  },
  orderStatus: {
    draft: 'Draf',
    sent: 'Terkirim',
    in_progress: 'Diproses',
    ready: 'Siap',
    served: 'Tersaji',
    billed: 'Ditagihkan',
    paid: 'Lunas',
    void: 'Void',
  },
  tableKind: {
    physical: 'Meja',
    virtual_quick: 'Cepat',
    virtual_takeaway: 'Takeaway',
    virtual_delivery: 'Delivery',
    virtual_kiosk: 'Kiosk',
  },
  orderType: { dine_in: 'Dine-in', takeaway: 'Takeaway', delivery: 'Delivery' },
  entryChannel: {
    pos: 'Kasir',
    kiosk: 'Kiosk',
    waiter_app: 'Pelayan',
    web: 'Web',
    qr_table: 'QR Meja',
    call_center: 'Call Center',
    marketplace: 'Marketplace',
  },
  priceChannel: {
    dine_in: 'Dine-in',
    takeaway: 'Takeaway',
    delivery_gofood: 'GoFood',
    delivery_grab: 'GrabFood',
    delivery_shopee: 'ShopeeFood',
    kiosk: 'Kiosk',
  },
  exemptReason: {
    complimentary: 'Komplimen',
    staff_meal: 'Makan Karyawan',
    promo_full: 'Promo 100%',
  },
  deviceRole: {
    /* ⭐ Kasir = TILL, boleh terima uang. Terminal = stasiun pesanan, TAK boleh (0051). */
    cashier: 'Kasir (till)',
    pos_terminal: 'Terminal Pesanan',
    kiosk: 'Kiosk',
    waiter_device: 'Perangkat Pelayan',
    kds_station: 'Stasiun KDS',
    kds_summary: 'Ringkasan KDS',
    queue_display: 'Layar Antrean',
    print_agent: 'Agen Cetak',
  },
  reasonCode: {
    /** Setor sebagian isi laci ke brankas di tengah sesi. */
    cash_drop: 'Setor sebagian',
    cash_variance: 'Selisih kas',
    customer_cancelled: 'Tamu membatalkan',
    customer_complaint: 'Keluhan tamu',
    customer_request: 'Permintaan tamu',
    /** ⭐ Perangkat pemegang kunci sunting mati — alasan paksa-buka yang sebenarnya. */
    device_down: 'Perangkat mati',
    duplicate_entry: 'Input ganda',
    item_unavailable: 'Item habis',
    /** Buka laci untuk menukar uang tamu — tak ada penjualan. */
    make_change: 'Tukar uang',
    /** ⭐ Kasir lupa menerapkan diskon sebelum menutup bill. */
    missed_discount: 'Diskon terlewat',
    kitchen_error: 'Kesalahan dapur',
    other: 'Lainnya',
    /** ⭐ Ambil/simpan barang pribadi (kunci, ponsel) — kejadian sehari-hari, bukan peristiwa uang. */
    personal_item: 'Barang pribadi',
    printer_failure: 'Printer bermasalah',
    reprint_copy: 'Cetak ulang',
    staff_offline_substitute: 'Pengganti saat luring',
    stock_opname: 'Opname stok',
    stock_transfer: 'Transfer stok',
    stock_waste: 'Barang rusak',
    system_error: 'Kesalahan sistem',
    test_transaction: 'Transaksi uji',
    training: 'Pelatihan',
    wrong_item: 'Salah item',
    wrong_price: 'Salah harga',
    wrong_qty: 'Salah jumlah',
    /** ⭐ Kasir salah pilih metode bayar — tunai dicatat QRIS, atau sebaliknya. */
    wrong_tender: 'Salah metode bayar',
  },
};

export const enumEn: typeof enumId = {
  tenderType: {
    cash: 'Cash',
    clearing: 'Card/QRIS', // T1: QRIS dipertahankan — standar nasional, bukan istilah generik
    liability_release: 'Voucher/Balance',
    non_tender: 'Complimentary',
    receivable: 'Tab/Receivable',
  },
  taxKind: {
    local_tax: 'Local Tax',
    vat: 'PPN', // ⛔ T1: JANGAN jadi "VAT" — laporan pajak Indonesia memakai istilah ini
    service_charge: 'Service Charge',
    withholding: 'Withholding',
  },
  orderStatus: {
    draft: 'Draft',
    sent: 'Sent',
    in_progress: 'In progress',
    ready: 'Ready',
    served: 'Served',
    billed: 'Billed',
    paid: 'Paid',
    void: 'Void', // T3
  },
  tableKind: {
    physical: 'Table',
    virtual_quick: 'Quick',
    virtual_takeaway: 'Takeaway', // T3
    virtual_delivery: 'Delivery',
    virtual_kiosk: 'Kiosk',
  },
  orderType: { dine_in: 'Dine-in', takeaway: 'Takeaway', delivery: 'Delivery' },
  entryChannel: {
    pos: 'Cashier',
    kiosk: 'Kiosk',
    waiter_app: 'Waiter',
    web: 'Web',
    qr_table: 'QR Table',
    call_center: 'Call Center',
    marketplace: 'Marketplace',
  },
  priceChannel: {
    dine_in: 'Dine-in',
    takeaway: 'Takeaway',
    delivery_gofood: 'GoFood',
    delivery_grab: 'GrabFood',
    delivery_shopee: 'ShopeeFood',
    kiosk: 'Kiosk',
  },
  exemptReason: {
    complimentary: 'Complimentary',
    staff_meal: 'Staff Meal',
    promo_full: '100% Promo',
  },
  deviceRole: {
    cashier: 'Cashier (till)',
    pos_terminal: 'Order Terminal',
    kiosk: 'Kiosk',
    waiter_device: 'Waiter Device',
    kds_station: 'KDS Station',
    kds_summary: 'KDS Summary',
    queue_display: 'Queue Display',
    print_agent: 'Print Agent',
  },
  reasonCode: {
    cash_drop: 'Cash drop',
    cash_variance: 'Cash variance',
    customer_cancelled: 'Customer cancelled',
    customer_complaint: 'Customer complaint',
    customer_request: 'Customer request',
    device_down: 'Device down',
    duplicate_entry: 'Duplicate entry',
    item_unavailable: 'Item unavailable',
    make_change: 'Make change',
    missed_discount: 'Missed discount',
    kitchen_error: 'Kitchen error',
    other: 'Other',
    personal_item: 'Personal item',
    printer_failure: 'Printer failure',
    reprint_copy: 'Reprint copy',
    staff_offline_substitute: 'Offline substitute',
    stock_opname: 'Stock count',
    stock_transfer: 'Stock transfer',
    stock_waste: 'Waste',
    system_error: 'System error',
    test_transaction: 'Test transaction',
    training: 'Training',
    wrong_item: 'Wrong item',
    wrong_price: 'Wrong price',
    wrong_qty: 'Wrong quantity',
    wrong_tender: 'Wrong payment method',
  },
};
